// admin.ts — 관리비 순수 계산: 일반관리비(내작/외작 차등) + 재료관리비.
//
// 백엔드 `apps/api/api/core/cost/admin.py` 와 동일한 수식/결과를 유지해야 한다
// (CLAUDE.md 패리티 — 한쪽 수정 시 양쪽 + 테스트 동시 수정).
//
// Excel 출처: CNC1/BOLT1/NUT1!R24(일반관리비), R26(재료관리비), O1(내작/외작).
//
// 수식:
//   일반관리비 (R24) = IF(내작, 제조원가 × 17%, 제조원가 × 10%)
//   재료관리비 (R26) = 재료비계(P10) × 2%
//
// 부품 속성 `사급구분`(내작 inhouse / 외작 outsourced)에 따라 일반관리비율이 분기한다.
//
// 입력 원가계는 #5/#2 모듈의 결과 total 을 받는다(여기서 재정의 금지):
//   제조원가: import { manufacturingCost } from "@/lib/cost/manufacturing"; → .manufacturingCost
//   재료비계: import { materialCost } from "@/lib/cost/material";           → .matCostTotal
//
// 본 모듈은 number-in 집계만 한다 (하드 컨트랙트 — #8 내부원가 가 호출):
//   import { overheadRates, type AdminResult } from "@/lib/cost/admin";
//   result.generalAdmin  = 일반관리비 (R24)
//   result.materialAdmin = 재료관리비 (R26)
//
// 일반관리비·재료관리비는 #8(내부원가)의 입력 — 단계별 필드를 결과 객체에 보존한다.

// 관리비율 (Excel R24/R26 비율). 부품 속성 `사급구분`에 따라 일반관리비율이 분기.
export const GENERAL_ADMIN_RATE_INHOUSE = 0.17; // 내작 일반관리비율
export const GENERAL_ADMIN_RATE_OUTSOURCED = 0.1; // 외작 일반관리비율
export const MATERIAL_ADMIN_RATE = 0.02; // 재료관리비율 (내작/외작 공통)

export interface AdminInput {
  manufacturingCost: number; // 제조원가 R23
  materialCost: number; // 재료비계 P10
  inhouse?: boolean; // 사급구분 — 내작(true, 기본) / 외작(false)
}

export interface AdminResult {
  manufacturingCost: number; // 제조원가 R23 (입력 보존)
  materialCost: number; // 재료비계 P10 (입력 보존)
  inhouse: boolean; // 사급구분 — 내작(true) / 외작(false)
  generalAdminRate: number; // 적용된 일반관리비율 (0.17 내작 / 0.10 외작)
  generalAdmin: number; // 일반관리비 R24 = 제조원가 × 일반관리비율
  materialAdmin: number; // 재료관리비 R26 = 재료비계 × 2%
}

// 관리비를 집계한다 (Excel R24/R26).
//   일반관리비 = 제조원가 × (내작 17% / 외작 10%)
//   재료관리비 = 재료비계 × 2%
export function overheadRates({
  manufacturingCost,
  materialCost,
  inhouse = true,
}: AdminInput): AdminResult {
  const generalAdminRate = inhouse
    ? GENERAL_ADMIN_RATE_INHOUSE
    : GENERAL_ADMIN_RATE_OUTSOURCED;
  const generalAdmin = manufacturingCost * generalAdminRate;
  const materialAdmin = materialCost * MATERIAL_ADMIN_RATE;
  return {
    manufacturingCost,
    materialCost,
    inhouse,
    generalAdminRate,
    generalAdmin,
    materialAdmin,
  };
}
